import { useState, useCallback, useEffect } from "react";
import { DocumentDetails } from "JS/Models";
import { DocumentsService } from "JS/Services/Documents";
import { config } from "JS/Config";
import { useAppDispatch, useAppSelector } from "JS/Redux/Store";
import {
    setGlobalDocumentsFavorites,
    setGlobalDocuments,
} from "JS/Redux/Documents";
import { useSnackbar } from "JS/Helpers/Snackbar";
import { messaging } from "JS/Helpers/UserMessaging";
import { mediaService } from "JS/Services/Media";

export const service = new DocumentsService();

export const useDocumentsDetails = () => {
    const dispatch = useAppDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = useState(false);
    const [docDetails, setDocDetails] = useState<DocumentDetails[]>(null);

    const showError = () => {
        enqueueSnackbar(messaging.common.error, {
            variant: "error",
        });
    };

    const refetch = useCallback(() => {
        setLoading(true);
        service
            .getDocumentDetails()
            .then((res) => {
                const sorted = res.sort((a, b) =>
                    a.Description.localeCompare(b.Description),
                );
                setDocDetails(sorted);
                dispatch(setGlobalDocuments(sorted));
            })
            .catch(() => showError())
            .finally(() => {
                setLoading(false);
            });
    }, []);

    useEffect(() => {
        refetch();
    }, [refetch]);

    return {
        refetch,
        docDetails,
        loading,
    };
};

export const useDocumentsFavoritesDetails = () => {
    const { documentUserFavorites } = useGlobalDocuments();
    const [loading, setLoading] = useState(false);
    const [docDetails, setDocDetails] = useState<DocumentDetails[]>(null);

    const refetch = useCallback(() => {
        setLoading(true);
        service
            .getDocumentDetails()
            .then((res) => {
                let filteredFavoritesDoc = res.filter((x) =>
                    documentUserFavorites?.some((z) => z.Name === x.Name),
                );
                const sortedDocuments = filteredFavoritesDoc?.sort((a, b) =>
                    a.Description.localeCompare(b.Description),
                );
                setDocDetails(sortedDocuments);
            })
            .catch((err) => {
                console.log({ err });
            })
            .finally(() => {
                setLoading(false);
            });
    }, []);

    useEffect(() => {
        refetch();
    }, [refetch]);

    return {
        refetch,
        docDetails,
        loading,
    };
};

export const useDocumentPdf = (pdfName: string) => {
    const [loading, setLoading] = useState(false);
    const [pdfUrl, setPdfUrl] = useState<string>(null);
    const [mediaSizeBytes, setMediaSizeBytes] = useState<number>(undefined);

    const refetch = useCallback((name: string) => {
        setLoading(true);
        const docsConfig = config.s3.documents;

        try {
            const url = new URL(docsConfig.baseUrl);
            const bucket = url.hostname.split(".")[0];
            const postfix = url.pathname;

            if (!bucket || !postfix || !name) {
                setLoading(false);
                return;
            }

            mediaService
                .getSignedUrl(bucket, postfix, name)
                .then((response) => {
                    setPdfUrl(response.media_url);
                    setMediaSizeBytes(response.media_size || 0);
                })
                .catch((err) => {
                    console.error("Error fetching document URL:", err);
                    setMediaSizeBytes(0);
                })
                .finally(() => setLoading(false));
        } catch (err) {
            console.error("Invalid documents base URL:", err);
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (pdfName) {
            refetch(pdfName);
        }
    }, [refetch, pdfName]);

    return {
        refetch,
        pdfUrl,
        loading,
        mediaSizeBytes,
    };
};

export const useGlobalDocuments = () => {
    const dispatch = useAppDispatch();
    const favDocuments = useAppSelector(
        (state) => state.documents,
    )?.documentFavorites;
    const documentFavorites = favDocuments ? favDocuments : {};
    const documentUserFavorites =
        documentFavorites && documentFavorites[`${config?.user?.memberId}`]
            ? documentFavorites[`${config?.user?.memberId}`]
            : [];

    const downloadedContentNids = useAppSelector(
        (state) => state?.download?.downloadedContentNids,
    );

    const allDocuments = useAppSelector(
        (state) => state?.documents?.documentsList,
    );

    return {
        documentFavorites,
        documentUserFavorites,
        markDocumentsFavorites: (documentsFavorite: DocumentDetails) => {
            const currentUserFavorites: DocumentDetails[] = documentFavorites[
                config.user.memberId
            ]
                ? documentFavorites[config.user.memberId]
                : [];
            const newMap = { ...documentFavorites };
            const newDocFavorites = [...currentUserFavorites]?.filter(
                (c) => c.Name !== documentsFavorite.Name,
            );
            newDocFavorites.push(documentsFavorite);
            newMap[config.user.memberId] = newDocFavorites;

            dispatch(setGlobalDocumentsFavorites(newMap));
        },
        unMarkDocumentsFavorites: (documentsFavorite: DocumentDetails) => {
            const currentUserFavorites: DocumentDetails[] = documentFavorites[
                config.user.memberId
            ]
                ? documentFavorites[config.user.memberId]
                : [];
            const newMap = { ...documentFavorites };
            const userSpecificDocs = [...currentUserFavorites];
            userSpecificDocs.findIndex(
                (x) => x.Name === documentsFavorite?.Name,
            ) !== -1 &&
                userSpecificDocs.splice(
                    userSpecificDocs.findIndex(
                        (x) => x.Name === documentsFavorite?.Name,
                    ),
                    1,
                );
            newMap[config.user.memberId] = userSpecificDocs;

            dispatch(setGlobalDocumentsFavorites(newMap));
        },
        downloadedContentNids,
        allDocuments,
    };
};
